import axios from 'axios';
import { GET_USERS, GET_USERS_MINIMAL, USERS_LOADING, UPDATE_USER, GET_USER, UPDATE_USER_SUCCESS, UPDATE_USER_FAIL, DELETE_USER } from './types';
import { tokenConfig } from './authActions';
import { returnErrors } from './errorActions';

export const getUsers = (filters = {}, options = {}) => (dispatch, getState) => {
  dispatch(setUsersLoading());
  
  // Build query parameters
  const params = new URLSearchParams();
  
  // Pagination options
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);
  if (options.sortBy) params.append('sortBy', options.sortBy);
  if (options.sortOrder) params.append('sortOrder', options.sortOrder);
  
  // Filters
  if (filters.status) params.append('status', filters.status);
  if (filters.role) {
    if (Array.isArray(filters.role)) {
      filters.role.forEach(r => params.append('role', r));
    } else {
      params.append('role', filters.role);
    }
  }
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  
  // Multi-select filters
  if (filters.names && filters.names.length > 0) {
    filters.names.forEach(name => params.append('names', name));
  }
  if (filters.emails && filters.emails.length > 0) {
    filters.emails.forEach(email => params.append('emails', email));
  }
  if (filters.countries && filters.countries.length > 0) {
    filters.countries.forEach(country => params.append('countries', country));
  }
  if (filters.universities && filters.universities.length > 0) {
    filters.universities.forEach(university => params.append('universities', university));
  }
  if (filters.groups && filters.groups.length > 0) {
    filters.groups.forEach(group => params.append('groups', group));
  }
  
  const queryString = params.toString();
  const url = queryString ? `/api/users?${queryString}` : '/api/users';
  
  axios
    .get(url, tokenConfig(getState))
    .then(res =>
      dispatch({
        type: GET_USERS,
        payload: res.data
      })
    )
    .catch(err =>
      dispatch(returnErrors(err.response.data, err.response.status))
    );
};

export const setUsersLoading = () => {
  return {
    type: USERS_LOADING
  };
};

// Get minimal users list for dropdowns/filters (students only)
export const getUsersMinimal = () => (dispatch, getState) => {
  dispatch(setUsersLoading());
  axios
    .get('/api/users/minimal', tokenConfig(getState))
    .then(res => dispatch({ type: GET_USERS_MINIMAL, payload: res.data }))
    .catch(err => dispatch(returnErrors(err.response?.data, err.response?.status)));
};

export const getUserFilterValues = () => (dispatch, getState) => {
  const url = `/api/users/filter-values`;
  
  return axios
    .get(url, tokenConfig(getState))
    .then(res => res.data.data)
    .catch(err => {
      console.error('Error fetching filter values:', err);
      return {
        names: [],
        emails: [],
        countries: [],
        groups: []
      };
    });
};

export const updateUser = (id, userData) => (dispatch, getState) => {
  dispatch({ type: UPDATE_USER });
  
  axios
    .put(`/api/users/${id}`, userData, tokenConfig(getState))
    .then(res => {
      dispatch({
        type: UPDATE_USER_SUCCESS,
        payload: res.data
      });
    })
    .catch(err => {
      dispatch(returnErrors(err.response.data, err.response.status, 'UPDATE_USER_FAIL'));
      dispatch({
        type: UPDATE_USER_FAIL
      });
    });
};

export const deleteUser = (id) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    axios
      .delete(`/api/users/${id}`, tokenConfig(getState))
      .then(res => {
        dispatch({
          type: DELETE_USER,
          payload: id
        });
        resolve(res.data);
      })
      .catch(err => {
        dispatch(returnErrors(err.response.data, err.response.status));
        reject(err);
      });
  });
};

export const getUser = (id) => (dispatch, getState) => {
  axios
    .get(`/api/users/${id}`, tokenConfig(getState))
    .then(res =>
      dispatch({
        type: GET_USER,
        payload: res.data
      })
    )
    .catch(err =>
      dispatch(returnErrors(err.response.data, err.response.status))
    );
};

// Change user's program (resets all data except name/email/password)
export const changeUserProgram = (userId, groupId, pathId) => (dispatch, getState) => {
  return axios
    .put(`/api/users/${userId}/change-program`, { groupId, pathId }, tokenConfig(getState))
    .then(res => {
      dispatch({
        type: UPDATE_USER_SUCCESS,
        payload: res.data.user
      });
      return res.data;
    })
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      throw err.response?.data || err;
    });
};

// Change user's pathway within their current program
export const changeUserPath = (userId, pathId) => (dispatch, getState) => {
  return axios
    .put(`/api/users/${userId}/change-path`, { pathId }, tokenConfig(getState))
    .then(res => {
      dispatch({
        type: UPDATE_USER_SUCCESS,
        payload: res.data.user
      });
      return res.data;
    })
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      throw err.response?.data || err;
    });
};

export const bulkDeactivateUsers = (userIds) => (dispatch, getState) => {
  return axios
    .post('/api/users/bulk-deactivate', { userIds }, tokenConfig(getState))
    .then(res => res.data)
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      throw err.response?.data || err;
    });
};

export const bulkActivateUsers = (userIds) => (dispatch, getState) => {
  return axios
    .post('/api/users/bulk-activate', { userIds }, tokenConfig(getState))
    .then(res => res.data)
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      throw err.response?.data || err;
    });
};

export const bulkDeleteUsers = (userIds) => (dispatch, getState) => {
  return axios
    .post('/api/users/bulk-delete', { userIds }, tokenConfig(getState))
    .then(res => res.data)
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      throw err.response?.data || err;
    });
};

export const bulkChangeProgram = (userIds, groupId) => (dispatch, getState) => {
  return axios
    .post('/api/users/bulk-change-program', { userIds, groupId }, tokenConfig(getState))
    .then(res => res.data)
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      throw err.response?.data || err;
    });
};

export const bulkChangePath = (userIds, pathId) => (dispatch, getState) => {
  return axios
    .post('/api/users/bulk-change-path', { userIds, pathId }, tokenConfig(getState))
    .then(res => res.data)
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      throw err.response?.data || err;
    });
};

export const adjustUserXP = (userId, amount, operation, description) => (dispatch, getState) => {
  return axios
    .post('/api/experiencepoints/adjust-xp', { userId, amount, operation, description }, tokenConfig(getState))
    .then(res => res.data)
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      throw err.response?.data || err;
    });
};

export const bulkAdjustXP = (userIds, amount, operation, description) => (dispatch, getState) => {
  return axios
    .post('/api/experiencepoints/bulk-adjust-xp', { userIds, amount, operation, description }, tokenConfig(getState))
    .then(res => res.data)
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      throw err.response?.data || err;
    });
};

export const bulkResetProgress = (userIds) => (dispatch, getState) => {
  return axios
    .post('/api/users/bulk-reset-progress', { userIds }, tokenConfig(getState))
    .then(res => res.data)
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      throw err.response?.data || err;
    });
};

export const checkEmailExists = (email) => (dispatch, getState) => {
  return axios
    .post('/api/users/check-email', { email }, tokenConfig(getState))
    .then(res => res.data)
    .catch(err => {
      throw err.response?.data || err;
    });
}; 
